import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AudienceSessionForm } from '@/components/audience/AudienceSessionForm';
import { ActiveAudienceSession } from '@/components/audience/ActiveAudienceSession';
import { AudienceStats } from '@/components/audience/AudienceStats';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { useAudienceSession } from '@/hooks/useAudienceSession';
import Header from '@/components/Header';
import { Radio, TrendingUp, Users } from 'lucide-react';
import { useParams } from 'react-router-dom';

export default function AudienceMode() {
  const { gameId } = useParams<{ gameId: string }>();
  const { user, supabaseUser, internalUser } = useAuthenticatedUser();
  const userType = supabaseUser ? 'supabase' : 'internal';
  
  const { activeSession, loading } = useAudienceSession(user?.id, gameId);
  const [activeTab, setActiveTab] = useState<string>('join');

  if (!user) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <Alert>
              <AlertDescription>
                Please sign in to join audience mode.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="flex items-center gap-3 mb-6">
            <Radio className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold">Audience Mode</h1>
              <p className="text-muted-foreground">
                Share how you're watching the game
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="join" className="flex items-center gap-2">
                <Radio className="w-4 h-4" />
                Join Session
              </TabsTrigger>
              <TabsTrigger value="stats" className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Live Stats
              </TabsTrigger>
              <TabsTrigger value="active" className="flex items-center gap-2" disabled={!activeSession}>
                <Users className="w-4 h-4" />
                My Session
              </TabsTrigger>
            </TabsList>

            <TabsContent value="join" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Join Audience Mode</CardTitle>
                  <CardDescription>
                    Let everyone know how you're experiencing the game
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AudienceSessionForm 
                    userId={user.id}
                    userType={userType}
                    gameId={gameId}
                    onSuccess={() => setActiveTab('active')}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="stats" className="space-y-6">
              {gameId ? (
                <AudienceStats gameId={gameId} />
              ) : (
                <Alert>
                  <AlertDescription>
                    Please select a game to view audience stats.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            <TabsContent value="active" className="space-y-6">
              {activeSession ? (
                <ActiveAudienceSession 
                  session={activeSession}
                  onSessionEnded={() => setActiveTab('join')}
                />
              ) : (
                <Alert>
                  <AlertDescription>
                    No active session found.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
